import React, {
  useMemo,
  useRef,
  useEffect,
  useState,
  useCallback,
} from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import FilesTreeView from '../FileManagement/FilesTreeView';
import UploadFilesButton from '../FileManagement/UploadFilesButton';
import AddFolderButton from '../FileManagement/AddFolderButton';
import { StylishNewSearchBar } from '../DesignSystems/New/StylishNewSearchBar';
import StylishNewSelect from '../DesignSystems/New/StylishNewSelect';
import {
  useGetAllFiles,
  FileOrFolder,
  FileScopeType,
} from '../FileManagement/hooks/useGetAllFiles';
import { WORKSPACE_TYPES } from '../../types/workspace';
import { isUUID } from 'utils/isUUID';
import {
  useAppSelector,
  selectGroup,
  selectGroupGuid,
  selectIncident,
  selectIncidents,
  selectAllExerciseWorkspaces,
} from '../../slices/commonSelectors';

interface ChatFileSelectorProps {
  workspaceId: string;
  selectedFileIds: string[];
  onFileSelectionChange: (fileIds: string[]) => void;
  userWorkspaceRoleName?: string;
  selectionText?: string; // Custom text for selection footer (default: "selected for AI analysis")
}

type WorkspaceEntry = {
  id: string;
  label: string;
  kind: 'org' | 'current' | 'other';
  scopeType?: typeof WORKSPACE_TYPES.ORG;
};

const ChatFileSelector: React.FC<ChatFileSelectorProps> = ({
  workspaceId,
  selectedFileIds,
  onFileSelectionChange,
  userWorkspaceRoleName,
  selectionText = 'selected for AI analysis',
}) => {
  // Fetch current workspace files eagerly — needed for auto-select-newly-uploaded
  // and for Select All over the always-visible "Current" section.
  const { data: filesData } = useGetAllFiles(workspaceId);

  const orgGuid = useAppSelector(selectGroupGuid) as string | undefined;
  const orgGroup = useAppSelector(selectGroup) as Group | undefined;
  const orgScopeApplies = !!orgGuid && orgGuid !== workspaceId;

  const incidents = useAppSelector(selectIncidents);
  const currentIncidentFromState = useAppSelector(selectIncident) as
    | IncidentAugmented
    | undefined;
  const exerciseWorkspaces = useAppSelector(selectAllExerciseWorkspaces);

  // Build the ordered workspace list: Org → Current → Other Incidents (alphabetical).
  const workspaceEntries = useMemo<WorkspaceEntry[]>(() => {
    const entries: WorkspaceEntry[] = [];
    if (orgScopeApplies && orgGuid) {
      entries.push({
        id: orgGuid,
        label: orgGroup?.group_name || '',
        kind: 'org',
        scopeType: WORKSPACE_TYPES.ORG,
      });
    }
    // Prefer the active currentIncident from state (most reliable); fall back to the
    // incidents list lookup and exercise workspaces. If the chat is org-scoped
    // (workspaceId === orgGuid), use the org name.
    const currentIncidentFromList = incidents.find(
      (inc) => inc.id === workspaceId
    );
    const currentExercise = exerciseWorkspaces?.find(
      (w) => w.id === workspaceId
    );
    const currentLabel =
      (currentIncidentFromState?.id === workspaceId
        ? currentIncidentFromState?.name
        : undefined) ||
      currentIncidentFromList?.name ||
      currentExercise?.displayName ||
      (orgGuid === workspaceId ? orgGroup?.group_name : undefined) ||
      '';
    entries.push({
      id: workspaceId,
      label: currentLabel,
      kind: 'current',
    });
    const others = incidents
      .filter((inc) => inc.id && inc.id !== workspaceId)
      .sort((a, b) =>
        (a.name ?? '').localeCompare(b.name ?? '', undefined, {
          numeric: true,
          sensitivity: 'base',
        })
      )
      .map<WorkspaceEntry>((inc) => ({
        id: inc.id,
        label: inc.name || '',
        kind: 'other',
      }));
    entries.push(...others);
    return entries;
  }, [
    orgGuid,
    orgGroup,
    orgScopeApplies,
    workspaceId,
    incidents,
    currentIncidentFromState,
    exerciseWorkspaces,
  ]);

  // Shared search across all sections.
  const [searchTerm, setSearchTerm] = useState('');
  const searchActive = searchTerm.trim().length > 0;

  // Per-section expand state. Org + Current are expanded by default; Others are collapsed.
  // When a search is active, every visible section is force-expanded so the user can see matches.
  const [expandedSections, setExpandedSections] = useState<
    Record<string, boolean>
  >(() => {
    const init: Record<string, boolean> = {};
    if (orgScopeApplies && orgGuid) init[orgGuid] = true;
    init[workspaceId] = true;
    return init;
  });

  // Workspace filter — single-select. null means "no filter, show all".
  const [filterWorkspaceId, setFilterWorkspaceId] = useState<string | null>(
    null
  );

  const isSectionVisible = useCallback(
    (id: string) => filterWorkspaceId === null || filterWorkspaceId === id,
    [filterWorkspaceId]
  );
  const isSectionExpanded = useCallback(
    (id: string) => searchActive || !!expandedSections[id],
    [searchActive, expandedSections]
  );

  const toggleSection = (id: string) => {
    setExpandedSections((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  type FilterOption = { label: string; value: string };
  const workspaceFilterOptions = useMemo<FilterOption[]>(
    () => workspaceEntries.map((e) => ({ label: e.label || '—', value: e.id })),
    [workspaceEntries]
  );
  const workspaceFilterValue = useMemo<FilterOption | null>(
    () =>
      workspaceFilterOptions.find((o) => o.value === filterWorkspaceId) ?? null,
    [workspaceFilterOptions, filterWorkspaceId]
  );
  const handleWorkspaceFilterChange = (value: unknown) => {
    const selected = value as FilterOption | null;
    setFilterWorkspaceId(selected?.value ?? null);
  };

  // Extract file IDs in the current workspace — used by Select All / Deselect All and
  // by the newly-uploaded auto-selection effect below.
  const currentWorkspaceFileIds = useMemo(() => {
    if (!filesData?.dataset) return [];
    return filesData.dataset
      .filter((item: FileOrFolder) => item.type === 'file')
      .map((file) => file.id);
  }, [filesData]);

  const previousFileIdsRef = useRef<string[]>([]);
  useEffect(() => {
    if (currentWorkspaceFileIds.length === 0) {
      previousFileIdsRef.current = [];
      return;
    }
    const previousIds = previousFileIdsRef.current;
    if (previousIds.length === 0) {
      previousFileIdsRef.current = currentWorkspaceFileIds;
      return;
    }
    const newFileIds = currentWorkspaceFileIds.filter(
      (id) => !previousIds.includes(id) && isUUID(id)
    );
    if (newFileIds.length > 0) {
      const updatedSelection = [
        ...new Set([...selectedFileIds, ...newFileIds]),
      ];
      onFileSelectionChange(updatedSelection);
    }
    previousFileIdsRef.current = currentWorkspaceFileIds;
  }, [currentWorkspaceFileIds, selectedFileIds, onFileSelectionChange]);

  return (
    <div className="chat-file-selector">
      <div className="px-3 pt-2 mb-2">
        <div className="mb-2">
          <StylishNewSearchBar
            value={searchTerm}
            onChangeSearchTerm={setSearchTerm}
            placeholder="Search files across workspaces"
          />
        </div>
        <div>
          <StylishNewSelect
            options={workspaceFilterOptions}
            value={workspaceFilterValue}
            onChange={handleWorkspaceFilterChange}
            isMulti={false}
            isClearable={true}
            placeholder="Filter by workspace"
          />
        </div>
      </div>

      {workspaceEntries
        .filter((entry) => isSectionVisible(entry.id))
        .map((entry) => (
          <WorkspaceSection
            key={entry.id}
            entry={entry}
            expanded={isSectionExpanded(entry.id)}
            onToggle={() => toggleSection(entry.id)}
            selectedFileIds={selectedFileIds}
            onSelectionChange={onFileSelectionChange}
            searchTerm={searchTerm}
            userWorkspaceRoleName={
              entry.kind === 'current' ? userWorkspaceRoleName : undefined
            }
          />
        ))}

      <div className="px-3 py-2 bg-light border-top">
        <small className="text-muted">
          {(() => {
            const count = selectedFileIds?.length ?? 0;
            const fileWord = count === 1 ? 'file' : 'files';
            return `${count} ${fileWord} ${selectionText}`;
          })()}
        </small>
      </div>
    </div>
  );
};

type WorkspaceSectionProps = {
  entry: WorkspaceEntry;
  expanded: boolean;
  onToggle: () => void;
  selectedFileIds: string[];
  onSelectionChange: (fileIds: string[]) => void;
  searchTerm: string;
  userWorkspaceRoleName?: string;
};

const WorkspaceSection: React.FC<WorkspaceSectionProps> = ({
  entry,
  expanded,
  onToggle,
  selectedFileIds,
  onSelectionChange,
  searchTerm,
  userWorkspaceRoleName,
}) => {
  // Only fetch when the section is expanded — keeps lazy-loading of "other
  // incidents" intact. React Query dedupes with the FilesTreeView below.
  const { data: sectionData } = useGetAllFiles(
    expanded ? entry.id : undefined,
    entry.scopeType as FileScopeType | undefined
  );
  const sectionFileIds = useMemo(() => {
    if (!sectionData?.dataset) return [] as string[];
    return sectionData.dataset
      .filter((item: FileOrFolder) => item.type === 'file')
      .map((file) => file.id);
  }, [sectionData]);

  const handleSelectAll = () => {
    onSelectionChange([...new Set([...selectedFileIds, ...sectionFileIds])]);
  };
  const handleDeselectAll = () => {
    const drop = new Set(sectionFileIds);
    onSelectionChange(selectedFileIds.filter((id) => !drop.has(id)));
  };
  const sectionSelectedCount = useMemo(
    () => sectionFileIds.filter((id) => selectedFileIds.includes(id)).length,
    [sectionFileIds, selectedFileIds]
  );

  return (
    <div className="border-top">
      <div className="d-flex align-items-center px-3 py-2">
        <button
          type="button"
          className="d-flex align-items-center flex-grow-1 bg-transparent border-0 text-start text-white p-0"
          onClick={onToggle}
          aria-expanded={expanded}
        >
          <i className="material-symbols-outlined text-white">
            {expanded ? 'keyboard_arrow_down' : 'keyboard_arrow_right'}
          </i>
          <span className="ms-1 fw-semibold text-white text-truncate">
            {entry.label}
          </span>
        </button>
        <div className="d-flex align-items-center gap-1 ms-2">
          <UploadFilesButton
            ownerId={entry.id}
            userWorkspaceRoleName={userWorkspaceRoleName}
            scopeType={entry.scopeType as FileScopeType | undefined}
            iconOnly={true}
            iconLabel={`Upload files to ${entry.label || 'workspace'}`}
            showNotificationText={false}
            showTooltip={false}
          />
          <AddFolderButton
            ownerId={entry.id}
            scopeType={entry.scopeType as FileScopeType | undefined}
            iconOnly={true}
            iconLabel={`Create folder in ${entry.label || 'workspace'}`}
          />
          {expanded && (
            <>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`select-all-${entry.id}`}>
                    Select all files
                  </Tooltip>
                }
              >
                <button
                  type="button"
                  onClick={handleSelectAll}
                  disabled={sectionFileIds.length === 0}
                  className="btn btn-link p-1 border-0 text-white"
                  aria-label="Select all files in this workspace"
                >
                  <i className="material-symbols-outlined">select_all</i>
                </button>
              </OverlayTrigger>
              <OverlayTrigger
                placement="top"
                overlay={
                  <Tooltip id={`deselect-all-${entry.id}`}>
                    Deselect all files
                  </Tooltip>
                }
              >
                <button
                  type="button"
                  onClick={handleDeselectAll}
                  disabled={sectionSelectedCount === 0}
                  className="btn btn-link p-1 border-0 text-white"
                  aria-label="Deselect all files in this workspace"
                >
                  <i className="material-symbols-outlined">deselect</i>
                </button>
              </OverlayTrigger>
            </>
          )}
        </div>
      </div>
      {expanded && (
        <FilesTreeView
          ownerId={entry.id}
          scopeType={entry.scopeType}
          userWorkspaceRoleName={userWorkspaceRoleName}
          selectionMode={true}
          selectedFileIds={selectedFileIds}
          onSelectionChange={onSelectionChange}
          showUploadButton={false}
          showCreateFolderButton={false}
          hideSearch={true}
          externalSearchTerm={searchTerm}
        />
      )}
    </div>
  );
};

export default ChatFileSelector;
